import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Search, Filter, CheckCircle, XCircle, Clock, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const EmailLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const limit = 25;
  const token = localStorage.getItem('token');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit, search, status: statusFilter, source: sourceFilter });
      const res = await fetch(`${API_BASE}/api/email-logs?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.data || []);
        setTotal(data.total || 0);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [page, search, statusFilter, sourceFilter, token]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / limit);

  const statusBadge = (s) => {
    if (s === 'success') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700"><CheckCircle size={11} />Sent</span>;
    if (s === 'error' || s === 'failed') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700"><XCircle size={11} />Failed</span>;
    if (s === 'simulated') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700"><Clock size={11} />Simulated</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{s}</span>;
  };

  return (
    <div className="space-y-4" data-testid="email-logs-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail size={20} className="text-teal-600" />
          <h2 className="text-lg font-bold text-gray-800">Email Logs</h2>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{total} total</span>
        </div>
        <button onClick={fetchLogs} className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"><RefreshCw size={12} /> Refresh</button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search recipient, subject, workflow..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-teal-500 outline-none"
            data-testid="email-logs-search" />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none" data-testid="email-logs-status-filter">
          <option value="">All Status</option>
          <option value="success">Sent</option>
          <option value="error">Failed</option>
          <option value="simulated">Simulated</option>
        </select>
        <select value={sourceFilter} onChange={e => { setSourceFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none" data-testid="email-logs-source-filter">
          <option value="">All Sources</option>
          <option value="workflow">Workflow</option>
          <option value="manual">Manual</option>
          <option value="test">Test</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Recipient</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Subject</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Source</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Workflow</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Provider</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Sent At</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-400">No email logs found</td></tr>
            ) : logs.map(log => (
              <tr key={log.id} className="hover:bg-gray-50" data-testid={`email-log-${log.id}`}>
                <td className="px-4 py-3 text-gray-800 font-medium truncate max-w-[180px]">{log.recipient}</td>
                <td className="px-4 py-3 text-gray-700 truncate max-w-[220px]">{log.subject}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    log.source === 'workflow' ? 'bg-blue-50 text-blue-700' :
                    log.source === 'manual' ? 'bg-teal-50 text-teal-700' :
                    'bg-purple-50 text-purple-700'
                  }`}>{log.source}</span>
                </td>
                <td className="px-4 py-3 text-gray-500 truncate max-w-[150px]">{log.workflow_name || '-'}</td>
                <td className="px-4 py-3">{statusBadge(log.status)}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{log.provider}</td>
                <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{log.sent_at ? new Date(log.sent_at).toLocaleString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">Page {page} of {totalPages}</p>
          <div className="flex gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="p-1.5 border rounded-lg disabled:opacity-30"><ChevronLeft size={14} /></button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="p-1.5 border rounded-lg disabled:opacity-30"><ChevronRight size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailLogs;
